import { lazy, type ComponentType } from 'react';

/**
 * Selbstheilende lazy-Imports (C0.1, Kundenbefund „Failed to fetch dynamically
 * imported module" nach Deploy).
 *
 * Ursache: Jedes Deploy erzeugt neue Content-Hashes. Ein offener Tab mit altem
 * index.html fordert beim Navigieren einen Chunk an, den es nicht mehr gibt —
 * der lazy-Import rejected und die ErrorBoundary zeigt eine Fehlerkarte, die
 * per „Erneut versuchen" nicht heilbar ist. Ein voller Reload holt das frische
 * index.html und behebt es IMMER — also machen wir den automatisch, genau
 * einmal (sessionStorage-Guard gegen Reload-Schleifen bei echten Ausfällen).
 */

const RELOAD_FLAG = 'sizilianer:chunk-reload';

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  // Chromium / Firefox / Safari formulieren den Fehler unterschiedlich.
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(
    msg,
  );
}

/**
 * Lädt die Seite genau EINMAL neu. Liefert false, wenn der Reload schon
 * verbraucht ist (dann soll der Aufrufer den Fehler normal anzeigen).
 */
export function reloadOnceForChunkError(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    return false; // Storage gesperrt → lieber Fehlerkarte als Reload-Schleife
  }
  window.location.reload();
  return true;
}

/** Nach einem erfolgreichen Chunk-Load ist der Guard wieder scharf. */
function clearReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* egal */
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory()
      .then((mod) => {
        clearReloadFlag();
        return mod;
      })
      .catch((err: unknown) => {
        if (isChunkLoadError(err) && reloadOnceForChunkError()) {
          // Seite lädt gerade neu — Promise bewusst nie auflösen, damit
          // Suspense den Loader zeigt statt kurz die Fehlerkarte.
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }),
  );
}
